/**
 * 替代品规则详情 Drawer 组件
 */

import React from 'react';
import { Drawer, Descriptions, Table, Tag, Button, Divider } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { SubstituteRuleDTO } from '../../../types/promotion-rule/substituteRule';
import {
  SubstituteRuleStatus,
  SubstituteRuleStatusOptions,
} from '../../../constants/promotionRule';



interface SubstituteRuleDetailProps {
  visible: boolean;
  record: SubstituteRuleDTO | null;
  onClose: () => void;
  onEdit: () => void;
}

const SubstituteRuleDetail: React.FC<SubstituteRuleDetailProps> = ({
  visible,
  record,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();

  if (!record) return null;

  // 获取状态标签
  const getStatusTag = (status: SubstituteRuleStatus) => {
    const option = SubstituteRuleStatusOptions.find(opt => opt.value === status);
    return <Tag color={option?.color}>{t(option?.label || status)}</Tag>;
  };

  // 匹配配置表格列
  const matchConfigColumns = [
    {
      title: t('promotionRule.form.matchConfig.matchType'),
      dataIndex: 'matchType',
      key: 'matchType',
      width: 140,
      render: (val: string) => t(`promotionRule.form.matchType.${val.toLowerCase()}`),
    },
    {
      title: t('promotionRule.form.matchConfig.matchRule'),
      dataIndex: 'matchRule',
      key: 'matchRule',
      width: 140,
      render: (val: string) => {
        const key = val.toLowerCase().replace(/_([a-z])/g, (g) => g[1].toUpperCase());
        return t(`promotionRule.form.matchRule.${key}`);
      },
    },
    {
      title: t('promotionRule.form.matchConfig.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      align: 'center' as const,
    },
  ];

  return (
    <Drawer
      title={t('promotionRule.detail.title')}
      placement="right"
      width={720}
      onClose={onClose}
      open={visible}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          {record.status === SubstituteRuleStatus.DISABLED && (
            <Button type="primary" icon={<EditOutlined />} onClick={onEdit} style={{ marginRight: 8 }}>
              {t('promotionRule.list.actions.edit')}
            </Button>
          )}
          <Button onClick={onClose}>
            {t('common.actions.close')}
          </Button>
        </div>
      }
    >
      {/* 主信息 */}
      <Descriptions column={2} bordered size="small">
        <Descriptions.Item label={t('promotionRule.detail.ruleCode')}>
          {record.ruleCode}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.ruleName')}>
          {record.ruleName}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.status')}>
          {getStatusTag(record.status)}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.priority')}>
          {record.priority}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.effectiveStartTime')}>
          {record.effectiveStartTime ? dayjs(record.effectiveStartTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.effectiveEndTime')}>
          {record.effectiveEndTime ? dayjs(record.effectiveEndTime).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.description')} span={2}>
          {record.description || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.creatorName')}>
          {record.creatorName}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.gmtCreate')}>
          {record.gmtCreate ? dayjs(record.gmtCreate).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.modifierName')}>
          {record.modifierName}
        </Descriptions.Item>
        <Descriptions.Item label={t('promotionRule.detail.gmtModified')}>
          {record.gmtModified ? dayjs(record.gmtModified).format('DD-MM-YYYY HH:mm:ss') : '-'}
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      {/* 匹配配置 */}
      <div style={{ marginTop: 16 }}>
        <h4 style={{ marginBottom: 16 }}>{t('promotionRule.form.matchConfig.title')}</h4>
        <Table
          rowKey="priority"
          columns={matchConfigColumns}
          dataSource={record.matchConfigs || []}
          pagination={false}
          size="small"
          scroll={{ x: 400 }}
          locale={{ emptyText: t('common.message.noData') }}
        />
      </div>
    </Drawer>
  );
};

export default SubstituteRuleDetail;
